/**
 * funnels.ts — API client for the Funnels resource (WEB-297 Funnel F3 FE).
 *
 * Route prefix: /projects/:projectId/funnels
 * Mirrors backend FunnelsController (WEB-296 F2).
 *
 * WEB-1050: импорт client НАМЕРЕННО через алиас '@/api/client', не './client':
 * jest.config маппит только '^@/api/client$' на мок без import.meta —
 * относительный импорт тянул реальный client.ts и ронял любой тест-сьют,
 * статически дошедший до funnels.ts (тот же приём, что в aiFunnels.ts).
 */
import { api } from '@/api/client';

/**
 * WEB-745: server-side pagination + search for GET /projects/:id/funnels.
 * limit/offset mirror backend PaginationQueryDto (limit 1..100, default 20;
 * offset default 0). Callers that need EVERY funnel (direction pickers)
 * use listOptions below (WEB-1406) — the old «pass limit:100» stopgap is
 * retired: it silently lost the tail on 101+ funnel projects.
 */
export interface FunnelListParams {
  limit?: number;
  offset?: number;
  search?: string;
}

/**
 * WEB-1406: lightweight option row for direction pickers —
 * GET .../funnels/options (mirrors QuizOptionItem/PaywallOptionItem,
 * WEB-745/WEB-1284). status — канонично строчный enum воронок
 * ('draft'/'active'/'broken'), но потребители сравнивают регистронезависимо
 * (урок WEB-1284: у квизов/пейволлов enum ЗАГЛАВНЫЙ). unpublishedAt —
 * пауза (WEB-416): воронка со status='active' и заданным unpublishedAt
 * не должна предлагаться направлением A/B-теста.
 */
export interface FunnelOptionItem {
  id: string;
  name: string;
  status: string;
  unpublishedAt: string | null;
}

/**
 * WEB-3749: тело `POST /projects/:projectId/funnels/blank` — зеркало
 * `CreateBlankFunnelDto` (`components['schemas']['CreateBlankFunnelDto']`)
 * без поля языка: окно его не шлёт (п. 51). Источник части без поля — «новая».
 */
export type BlankPieceSource = 'new' | 'library' | 'none';

export interface CreateBlankFunnelBody {
  name?: string;
  quizSource?: BlankPieceSource;
  quizId?: string;
  paywallSource?: BlankPieceSource;
  paywallId?: string;
}

export const funnelsApi = {
  list: (projectId: string, params?: FunnelListParams) =>
    api.get(`/projects/${projectId}/funnels`, { params }),

  // WEB-1406: unpaginated {id,name,status,unpublishedAt} list for the
  // smart-links whole-funnel direction picker (mirrors quizzesApi/
  // paywallsApi.listOptions).
  listOptions: (projectId: string) =>
    api.get<FunnelOptionItem[]>(`/projects/${projectId}/funnels/options`),

  get: (projectId: string, id: string) =>
    api.get(`/projects/${projectId}/funnels/${id}`),

  create: (projectId: string, data: Record<string, unknown>) =>
    api.post(`/projects/${projectId}/funnels`, data),

  // WEB-3749 (окно «Новая воронка»): воронка с квизом и пейволлом одним
  // запросом и одной транзакцией сервера (WEB-3818, выбор частей — WEB-3929).
  // Языка в теле нет — его ставит сервер (решение владельца 25.10, п. 51).
  createBlank: (projectId: string, data: CreateBlankFunnelBody) =>
    api.post(`/projects/${projectId}/funnels/blank`, data),

  update: (projectId: string, id: string, data: Record<string, unknown>) =>
    api.patch(`/projects/${projectId}/funnels/${id}`, data),

  delete: (projectId: string, id: string) =>
    api.delete(`/projects/${projectId}/funnels/${id}`),

  publish: (projectId: string, id: string) =>
    api.post(`/projects/${projectId}/funnels/${id}/publish`),

  // WEB-416: mirrors publish — pauses the funnel (sets unpublishedAt, status unchanged).
  unpublish: (projectId: string, id: string) =>
    api.post(`/projects/${projectId}/funnels/${id}/unpublish`),

  // WEB-415: move/copy funnel (+ cascade to its quiz/paywall pieces) to
  // another project in the same org. Mirrors paywallsApi.move/.copy and
  // smartLinksApi.move.
  move: (projectId: string, id: string, targetProjectId: string) =>
    api.post(`/projects/${projectId}/funnels/${id}/move`, { targetProjectId }),

  copy: (projectId: string, id: string, targetProjectId: string) =>
    api.post(`/projects/${projectId}/funnels/${id}/copy`, { targetProjectId }),

  // WEB-3963: копия воронки со всеми частями черновиком в ТОМ ЖЕ проекте,
  // без публикации и домена (ручка сервера 56be91228). Тела нет; ответ —
  // новая воронка (FunnelResponseDto, 201), имя копии даёт сервер.
  duplicate: (projectId: string, id: string) =>
    api.post(`/projects/${projectId}/funnels/${id}/duplicate`),

  // WEB-415 (Р5): read-only preview for the move confirmation modal — lists
  // the funnel's pieces and any other funnel sharing them, and whether a
  // real move() call would be blocked (409).
  movePreview: (projectId: string, id: string, targetProjectId: string) =>
    api.get(`/projects/${projectId}/funnels/${id}/move-preview`, {
      params: { targetProjectId },
    }),
};
